import { getErrorMessage } from "@brains/utils/error";
/**
 * ContentScheduler - Cron-based scheduler for content pipeline queues
 *
 * Implements Component Interface Standardization pattern.
 * Uses a pluggable backend for scheduling (Bun in production).
 *
 * Publishes queued entities through registered providers and supports
 * generation scheduling for automatic draft creation.
 */

import type { PublishResult } from "@brains/contracts";
import { sendPublishCompleted, sendPublishFailed } from "./scheduler-publish";
import type { PublishDeps } from "./scheduler-publish";
import {
  sendGenerationCompleted,
  sendGenerationFailed,
} from "./scheduler-generation";
import type { GenerationDeps } from "./scheduler-generation";
import type {
  SchedulerConfig,
  ResolvedSchedulerConfig,
} from "./types/scheduler";
import { PublishScheduleRunner } from "./scheduler-publish-runner";
import { GenerationScheduleRunner } from "./scheduler-generation-runner";

// Re-export all types from types/scheduler for backward compatibility
export type {
  SchedulerConfig,
  ResolvedSchedulerConfig,
  GenerateExecuteEvent,
  GenerationConditionResult,
  PublishSuccessEvent,
  PublishFailedEvent,
} from "./types/scheduler";

export class ContentScheduler {
  private config: ResolvedSchedulerConfig;
  private publishRunner: PublishScheduleRunner;
  private generationRunner: GenerationScheduleRunner;
  private running = false;
  private stopPromise: Promise<void> | null = null;

  public static createFresh(config: SchedulerConfig): ContentScheduler {
    return new ContentScheduler(config);
  }

  private constructor(config: SchedulerConfig) {
    this.config = {
      ...config,
      entitySchedules: config.entitySchedules ?? {},
      generationSchedules: config.generationSchedules ?? {},
      generationConditions: config.generationConditions ?? {},
    };

    this.validateCronExpressions();

    this.publishRunner = new PublishScheduleRunner({
      config: this.config,
      getPublishDeps: (): PublishDeps => this.publishDeps,
      isRunning: (): boolean => this.running,
    });
    this.generationRunner = new GenerationScheduleRunner({
      config: this.config,
      getGenerationDeps: (): GenerationDeps => this.generationDeps,
      isRunning: (): boolean => this.running,
    });
  }

  // -------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------

  public async start(): Promise<void> {
    if (this.running) return;
    if (this.stopPromise) {
      await this.stopPromise;
      this.stopPromise = null;
    }

    this.running = true;

    this.publishRunner.start();
    this.generationRunner.start();
  }

  public stop(): Promise<void> {
    if (this.stopPromise) return this.stopPromise;

    this.running = false;
    this.stopPromise = Promise.all([
      this.publishRunner.stop(),
      this.generationRunner.stop(),
    ]).then(() => undefined);
    return this.stopPromise;
  }

  public isRunning(): boolean {
    return this.running;
  }

  // -------------------------------------------------------------------
  // Public publish reporting
  // -------------------------------------------------------------------

  public completePublish(
    entityType: string,
    entityId: string,
    result: PublishResult,
  ): void {
    sendPublishCompleted(entityType, entityId, result, this.publishDeps);
  }

  public failPublish(
    entityType: string,
    entityId: string,
    error: string,
  ): void {
    sendPublishFailed(entityType, entityId, error, this.publishDeps);
  }

  // -------------------------------------------------------------------
  // Generation scheduling
  // -------------------------------------------------------------------

  public completeGeneration(entityType: string, entityId: string): void {
    sendGenerationCompleted(entityType, entityId, this.config.messageBus);
  }

  public failGeneration(entityType: string, error: string): void {
    sendGenerationFailed(entityType, error, this.config.messageBus);
  }

  // -------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------

  private get entitySchedules(): Record<string, string> {
    return this.config.entitySchedules;
  }

  private get generationSchedules(): Record<string, string> {
    return this.config.generationSchedules;
  }

  private get publishDeps(): PublishDeps {
    return {
      retryTracker: this.config.retryTracker,
      messageBus: this.config.messageBus,
      publishExecutor: this.config.publishExecutor,
      onPublish: this.config.onPublish,
      onFailed: this.config.onFailed,
    };
  }

  private get generationDeps(): GenerationDeps {
    return {
      logger: this.config.logger,
      messageBus: this.config.messageBus,
      generationConditions: this.config.generationConditions,
      onCheckGenerationConditions: this.config.onCheckGenerationConditions,
      onGenerate: this.config.onGenerate,
    };
  }

  private validateCronExpressions(): void {
    for (const [entityType, cronExpr] of Object.entries(this.entitySchedules)) {
      this.validateCronExpression(entityType, cronExpr, "publish");
    }

    for (const [entityType, cronExpr] of Object.entries(
      this.generationSchedules,
    )) {
      this.validateCronExpression(entityType, cronExpr, "generation");
    }
  }

  private validateCronExpression(
    entityType: string,
    cronExpr: string,
    scheduleType: string,
  ): void {
    try {
      this.config.backend.validateCron(cronExpr);
    } catch (error) {
      throw new Error(
        `Invalid ${scheduleType} cron expression for ${entityType}: "${cronExpr}" - ${getErrorMessage(error)}`,
        { cause: error },
      );
    }
  }
}
